import { z } from "zod";
import { emailStatusSchema } from "../event-reports/emailStatus.js";
import { REPORT_MAX_PHOTOS as RAMONAGE_MAX_PHOTOS, REPORT_PHOTO_MAX_BYTES as RAMONAGE_PHOTO_MAX_BYTES, reportPhotoInputSchema as ramonagePhotoInputSchema, reportPhotoSchema as ramonagePhotoSchema, } from "../event-reports/reportPhoto.js";
// ==================== Reference Data Constants ====================
/** Apparatus types grouped by fuel category */
export const APPAREIL_TYPES = [
    {
        category: "Bois",
        items: ["Poêle à bois", "Insert à bois", "Cheminée", "Foyer ouvert", "Cuisinière", "Autre"],
    },
    {
        category: "Granulés / Pellets",
        items: [
            "Poêle à granulés",
            "Chaudière à granulés",
            "Insert à granulés",
            "Cuisinière",
            "Autre",
        ],
    },
    {
        category: "Gaz",
        items: ["Cheminée gaz", "Insert gaz", "Chaudière gaz (hors ventouse)", "Autre"],
    },
    {
        category: "Fioul",
        items: ["Chaudière fioul", "Autre"],
    },
];
/** Maps apparatus type to its fuel type */
export const COMBUSTIBLE_MAP = {
    "Poêle à bois": "Bois",
    "Insert à bois": "Bois",
    Cheminée: "Bois",
    "Foyer ouvert": "Bois",
    "Poêle à granulés": "Granulés / Pellets",
    "Chaudière à granulés": "Granulés / Pellets",
    "Insert à granulés": "Granulés / Pellets",
    "Cheminée gaz": "Gaz",
    "Insert gaz": "Gaz",
    "Chaudière gaz (hors ventouse)": "Gaz",
    "Chaudière fioul": "Fioul",
};
export const CUSTOM_APPAREIL_TYPE_MESSAGE = "Veuillez préciser le type d'appareil.";
export function refineCustomAppareilType(data, ctx) {
    if (data.appareilType === "Autre" && !data.appareilTypeCustom?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: CUSTOM_APPAREIL_TYPE_MESSAGE,
            path: ["appareilTypeCustom"],
        });
    }
}
export function getAppareilTypeLabel(appareil) {
    return appareil.appareilType === "Autre"
        ? appareil.appareilTypeCustom?.trim() || "Autre"
        : appareil.appareilType;
}
export const EMPLACEMENTS = [
    "Salon / Séjour",
    "Cuisine",
    "Chambre",
    "Bureau",
    "Sous-sol / Cave",
    "Garage",
    "Atelier",
    "Autre",
];
export const CONDUIT_TYPES = [
    "Maçonné",
    "Tubé inox",
    "Tubé inox isolé",
    "Double paroi isolé",
    "Fibrociment",
    "Autre",
];
export const ARRIVEE_AIR_OPTIONS = [
    "Directe (grille extérieure)",
    "Indirecte (ventilation pièce)",
    "Non vérifiable",
    "Absente",
];
export const NATURE_INTERVENTION_OPTIONS = [
    "Ramonage",
    "Entretien / Nettoyage",
    "Contrôle / Vérification",
    "Débistrage",
    "Tubage",
    "Mise en conformité",
    "Autre",
];
/** Nettoyage actions with their available tools */
export const NETTOYAGE_ACTIONS = [
    {
        action: "Manuelle",
        outils: ["Hérisson nylon", "Hérisson acier", "Canne de ramonage", "Buisson"],
    },
    {
        action: "Mécanique",
        outils: ["Hérisson rotatif électrique", "Débistreuse rotative", "Système rotatif à ressort"],
    },
    {
        action: "Chimique",
        outils: ["Bûche de ramonage", "Poudre chimique", "Autre produit"],
    },
];
export const NETTOYAGE_METHODES_ACCES = [
    "Par le bas",
    "Par le haut",
    "Par une trappe",
    "Mixte (bas + haut)",
];
export const ETAT_CONDUIT_OPTIONS = [
    "Bon état",
    "État correct",
    "Usure visible",
    "Fissures / Défauts",
    "Nécessite intervention",
];
export const TYPE_SUIES_OPTIONS = [
    "Suies fines et volatiles",
    "Suies carbones en « paillettes »",
    "Suies bistrées en « Grains/Morceaux »",
    "Suies brûlées",
];
export const VOLUME_SUIES_OPTIONS = ["Faible", "Modéré", "Important", "Très important"];
export const SUIES_BOTH_OR_NEITHER_MESSAGE = "Renseignez le type et le volume de suies, ou aucun des deux";
/**
 * Prédicat de couplage type/volume de suies, partagé entre `appareilSchema` (validation
 * serveur) et `step4Schema` (validation form, packages-web/client-records) : une divergence
 * entre les deux resterait invisible tant que chacun a ses propres tests.
 */
export function refineSuiesCoupling(data, ctx) {
    if (!!data.typeSuies !== !!data.volumeSuies) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: SUIES_BOTH_OR_NEITHER_MESSAGE,
            path: ["typeSuies"],
        });
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: SUIES_BOTH_OR_NEITHER_MESSAGE,
            path: ["volumeSuies"],
        });
    }
}
// ==================== Zod Schemas ====================
// Nombre d'appareils autorisés dans une même attestation (#1236).
export const MIN_APPAREILS = 1;
export const MAX_APPAREILS = 5;
// Borne la longueur des remarques : un texte non borné peut faire déborder le bloc appareil
// sur une page supplémentaire, décalant la mention "Suite page k" de RamonagePdfReport.
export const REMARQUES_MAX_LENGTH = 1000;
export { REPORT_MAX_PHOTOS as RAMONAGE_MAX_PHOTOS, REPORT_PHOTO_MAX_BYTES as RAMONAGE_PHOTO_MAX_BYTES, REPORT_PHOTO_CAPTION_MAX_LENGTH as RAMONAGE_PHOTO_CAPTION_MAX_LENGTH, REPORT_PHOTO_CAPTION_MAX_NEWLINES as RAMONAGE_PHOTO_CAPTION_MAX_NEWLINES, REPORT_PDF_PREFLIGHT_MAX_BYTES as RAMONAGE_PDF_PREFLIGHT_MAX_BYTES, reportPhotoMetadataSchema as ramonagePhotoMetadataSchema, reportPhotoSchema as ramonagePhotoSchema, reportPhotoInputSchema as ramonagePhotoInputSchema, } from "../event-reports/reportPhoto.js";
export const RAMONAGE_PHOTOS_MAX_TOTAL_BYTES = MAX_APPAREILS * RAMONAGE_MAX_PHOTOS * RAMONAGE_PHOTO_MAX_BYTES;
export const RAMONAGE_PDF_MAX_BYTES = 6 * 1024 * 1024;
/**
 * Statut d'envoi email d'une attestation, stocké dans `ReportFile.data.emailStatus`
 * (pas de colonne dédiée). `sent`/`skipped`/`failed` viennent de la résolution synchrone de
 * `RamonageService.send()` ; `bounced` est posé plus tard, de façon asynchrone, par le webhook
 * Postmark (hard bounce, boîte pleine, serveur indisponible, message expiré ou signalement spam
 * — toutes ces issues retombent sur ce même statut visible). Absent/`null` = jamais renseigné
 * (attestation créée avant ce mécanisme, ou jamais envoyée) — traité comme `skipped` à l'affichage. (#1270)
 */
// Contenu par appareil (#1236) : installation, intervention et constat — remarques et vacuité incluses.
const appareilFieldsSchema = z.object({
    // Appareil (FR-2)
    appareilType: z.string(),
    appareilTypeCustom: z.string().optional(),
    appareilEmplacement: z.string(),
    appareilMarque: z.string().optional(),
    appareilModele: z.string().optional(),
    appareilPuissance: z.string().optional(),
    appareilNumeroSerie: z.string().optional(),
    appareilDateInstallation: z.string().optional(),
    appareilGarantie: z.string().optional(),
    combustible: z.string(),
    conduitType: z.string(),
    arriveeAir: z.string(),
    // Rapport intervenant (FR-3)
    natureIntervention: z.array(z.string()),
    controleVisuelAppareil: z.enum(["oui", "non"]),
    controleVisuelConduit: z.enum(["oui", "non", "partiel"]),
    controleVisuelSouche: z.enum(["oui", "non"]),
    // Nettoyage
    nettoyageAction: z.string(),
    nettoyageOutil: z.string(),
    nettoyageMethodeAcces: z.array(z.string()),
    tirage: z.enum(["conforme", "nonConforme", "nonVerifie"]),
    etatConduit: z.string(),
    typeSuies: z.string().optional(),
    volumeSuies: z.string().optional(),
    aspirationSuies: z.boolean(),
    remarques: z.string().max(REMARQUES_MAX_LENGTH).optional(),
    photos: z
        .array(ramonagePhotoSchema)
        .max(RAMONAGE_MAX_PHOTOS, "5 photos maximum par appareil.")
        .default([]),
    attestationVacuite: z.boolean(),
});
export const appareilSchema = appareilFieldsSchema
    .superRefine(refineSuiesCoupling)
    .superRefine(refineCustomAppareilType);
export const appareilInputSchema = appareilFieldsSchema
    .extend({
    photos: z
        .array(ramonagePhotoInputSchema)
        .max(RAMONAGE_MAX_PHOTOS, "5 photos maximum par appareil.")
        .default([]),
})
    .superRefine(refineSuiesCoupling)
    .superRefine(refineCustomAppareilType);
export const certificatRamonageSchema = z
    .object({
    date: z.string(),
    certificateNumber: z.string(),
    // Un seul document, une seule paire de signatures pour 1 à 5 appareils (#1236).
    appareils: z.array(appareilSchema).min(MIN_APPAREILS).max(MAX_APPAREILS),
    // Signatures (FR-4) — globales à l'attestation
    signatureTechnicien: z.string(),
    signatureTechnicienDate: z.string(),
    signatureClient: z.string().optional(),
    signatureClientDate: z.string().optional(),
    clientRefuseSigner: z.boolean().default(false),
    // Statut d'envoi : jamais envoyé par le formulaire de création, posé par le
    // backend (send()/resend()/webhook de bounce) — d'où optionnel dans ce schéma partagé. (#1270)
    emailStatus: emailStatusSchema.nullable().optional(),
    emailMessage: z.string().nullable().optional(),
})
    .refine((data) => data.signatureClient || data.clientRefuseSigner, {
    message: "Client must either sign or explicitly refuse",
    path: ["signatureClient"],
});
/**
 * Payload schema : sans `certificateNumber` (généré serveur), sans `emailStatus`/`emailMessage`
 * (jamais posés par le client — un payload de création qui les porterait serait ignoré ici,
 * pas juste toléré, pour empêcher un client de forger un statut d'envoi jamais atteint). (#1270)
 */
export const certificatRamonagePayloadSchema = certificatRamonageSchema
    .innerType()
    .omit({ certificateNumber: true, emailStatus: true, emailMessage: true })
    .extend({
    appareils: z.array(appareilInputSchema).min(MIN_APPAREILS).max(MAX_APPAREILS),
    signatureModelFingerprint: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .nullable()
        .optional(),
    conserveSignatureTechnicien: z.boolean().optional(),
    signatureTechnicienSource: z.enum(["drawn", "applied"]).optional(),
    clientAttemptId: z.string().uuid().optional(),
});
export const certificatRamonageUploadPayloadSchema = z.object({
    sendEmail: z.enum(["true", "false"]).default("false"),
});
/**
 * Projection minimale d'un certificat pour la section Documents de la fiche client (#1229) —
 * délibérément sans les champs métier du certificat (signatures, appareil…), même traitement
 * que `CertificatRamonagePrefill` ci-dessus pour `findLatestByCustomerId` (#924).
 */
export const CertificatRamonageDocumentSchema = z.object({
    eventId: z.string(),
    certificateNumber: z.string(),
    fileName: z.string(),
    attestationDate: z.coerce.date(),
    eventDate: z.coerce.date(),
    /** Nombre d'appareils du certificat (1 à 5, cf. `certificatRamonageSchema.appareils`). */
    appareilCount: z.number().int().positive(),
    emailStatus: emailStatusSchema.nullable().optional(),
    hasFile: z.boolean(),
});
//# sourceMappingURL=certificate.js.map